import { formatUtcTimeRange } from './date'

export const capitalize = str => {
  if (!str) return ''
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

export const debounce = (func, wait) => {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

export const darkenHexColor = (hex, amount) => {
  const num = parseInt(hex.replace('#', ''), 16)
  const r = Math.max(0, ((num >> 16) & 255) * (1 - amount))
  const g = Math.max(0, ((num >> 8) & 255) * (1 - amount))
  const b = Math.max(0, (num & 255) * (1 - amount))
  return `#${Math.round(r).toString(16).padStart(2, '0')}${Math.round(g).toString(16).padStart(2, '0')}${Math.round(b).toString(16).padStart(2, '0')}`
}

export const generateId = () => {
  return Math.random().toString(36).substr(2, 9)
}

export const isEmpty = value => {
  if (value === null || value === undefined) return true
  if (typeof value === 'string') return value.trim().length === 0
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === 'object') return Object.keys(value).length === 0
  return false
}

export const calculateDistanceInMiles = (lat1, lon1, lat2, lon2) => {
  const toRad = angle => (angle * Math.PI) / 180

  const R = 3958.8 // Radius of Earth in miles
  const dLat = toRad(lat2 - lat1)
  const dLon = toRad(lon2 - lon1)

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2)

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  const distance = R * c

  return parseFloat(distance.toFixed(1))
}

export const buildResultItem = (item, props = {}) => {
  const { latitude, longitude } = props
  let distanceValue = null
  if (latitude && longitude && item.latitude && item.longitude) {
    const userLat = parseFloat(latitude)
    const userLon = parseFloat(longitude)
    const scheduleLat = parseFloat(item.latitude)
    const scheduleLon = parseFloat(item.longitude)
    distanceValue = calculateDistanceInMiles(
      userLat,
      userLon,
      scheduleLat,
      scheduleLon
    )
  }

  let address = item.address
  if (item.city) address = address + ', ' + item.city
  if (item.state) address = address + ', ' + item.state
  if (item.zip_code) address = address + ' ' + item.zip_code
  if (item.site_type) address = `${address} (${item.site_type})`

  return {
    ...item,
    name: item.name,
    sponsoredBy: item.sponsored_by,
    secondaryName: item.secondary_name,
    distance: distanceValue ? distanceValue : null,
    hours: formatUtcTimeRange(item.start_time, item.end_time),
    address: address,
    spotsAvailable: item.available_slots_count,
  }
}

export function isScheduleRowBookable(rawRow) {
  const slots = Number(rawRow?.available_slots_count)
  return !Number.isNaN(slots) && slots > 0
}

export const buildDescriptionItem = item => {
  return {
    title: item.name,
    description: item.description,
    phone: item.phone_number,
    website: item.website,
    iconUrl: item.iconUrl,
  }
}

export const getLeastDistanceWithValue = (
  operations,
  userLatitude,
  userLongitude
) => {
  if (!userLatitude || !userLongitude || !operations || !operations.length)
    return { text: '', value: Infinity }

  const leastDistance = operations.reduce((min, operation) => {
    const distance = calculateDistanceInMiles(
      userLatitude,
      userLongitude,
      operation.latitude,
      operation.longitude
    )
    return Math.min(min, distance)
  }, Infinity)

  if (leastDistance === Infinity || isNaN(leastDistance))
    return { text: '', value: Infinity }

  return { text: `${leastDistance.toFixed(1)} mi`, value: leastDistance }
}

/**
 * Extracts validation errors from a yup ValidationError
 * @param {Error} error - The error object (typically from yup validation)
 * @returns {Object|null} - An object with field paths as keys and error messages as values, or null if not a ValidationError
 */
export const extractValidationErrors = error => {
  if (error?.name !== 'ValidationError') {
    return null
  }

  const fieldErrors = {}

  if (Array.isArray(error.inner)) {
    error.inner.forEach(item => {
      if (item.path && !fieldErrors[item.path]) {
        fieldErrors[item.path] = item.message
      }
    })
  } else if (error.path) {
    fieldErrors[error.path] = error.message
  }

  return Object.keys(fieldErrors).length > 0 ? fieldErrors : null
}

export const getDonorFullName = donor => {
  const fullName = []
  if (donor?.first_name) fullName.push(donor.first_name)
  if (donor?.last_name) fullName.push(donor.last_name)

  return fullName.length > 0 ? fullName.join(' ') : 'Donor'
}

/**
 * Triggers a browser download for a Blob returned from a file export endpoint.
 */
export const downloadCsv = (rows, headers, fileName) => {
  const escapeCell = value => {
    const str = value == null ? '' : String(value)
    if (/[",\n]/.test(str)) {
      return `"${str.replace(/"/g, '""')}"`
    }
    return str
  }

  const headerLine = headers.map(header => escapeCell(header.label)).join(',')
  const bodyLines = rows.map(row =>
    headers.map(header => escapeCell(row[header.key])).join(',')
  )
  const csv = [headerLine, ...bodyLines].join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  downloadBlob(blob, fileName)
}

export const downloadBlob = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

/**
 * Tracks DailyStory conversion when an appointment is confirmed
 * Conversion value is set to 100.00
 */
export const trackDailyStoryConversion = () => {
  const conversionValue = 100.0
  if (typeof window !== 'undefined' && window.Ds) {
    try {
      window.Ds.conversion(
        'a5d28095-eae1-4d9a-a47c-01a5385cd315',
        conversionValue
      )
    } catch (error) {
      console.error('Error tracking DailyStory conversion:', error)
    }
  }
}
